import { css } from '@emotion/react'
import { Badge, Tooltip, CircularProgress } from '@mui/material'
import { colors } from '@global/constants'

interface Props {
  isLoading: boolean
  title: string
  count?: number
  color?: string | null
  dotOnly?: boolean
  tooltip?: string | null
}

/**
 * Show a MUI Badge with title, optional badgeContent, and custom styling.
 * - Show as a dot only, or with a count.
 * @param props React Props
 * @param {number} props.count The count to show as badgeContent
 * @param {boolean} props.isLoading If the badgeContent is loading
 * @param {string} props.title The badge text.
 * @param {string | null} props.color The color of the badge.
 * @param {boolean} props.dotOnly If a dot is shown only and no badgeContent.
 * @param {string | null} props.tooltip The tooltip text.
 */
export const TitleWithBadge = ({
  count = 0,
  isLoading,
  title,
  color = null,
  dotOnly = false,
  tooltip = null,
}: Props) => {
  const hidden = isLoading || count === 0
  const badgeContent = getBadgeContent()

  const styles = {
    dotOnly: css`
      .MuiBadge-badge {
        background-color: ${color};
        height: 8px;
        width: 8px;
        padding: 0;
        min-width: 8px;
        right: -3px;
      }
    `,
    withCount: css`
      .MuiBadge-badge {
        right: -7px;
        top: 0;
        font-size: 10px;
        color: ${colors.white};
        background-color: ${hidden ? 'transparent' : colors.blue.smokey};
      }
    `,
  }

  return (
    <Badge
      badgeContent={badgeContent}
      css={dotOnly ? styles.dotOnly : styles.withCount}
      data-testid="badge"
      slotProps={{
        badge: {
          'data-testid': 'badge-counter',
        },
      }}
    >
      {tooltip ? (
        <Tooltip title={tooltip} placement="bottom" enterDelay={250} data-testid="title-tooltip">
          <span data-testid={`wrapped-title-${title}`}>{title}</span>
        </Tooltip>
      ) : (
        <span data-testid={`wrapped-title-${title}`}>{title}</span>
      )}
    </Badge>
  )

  function getBadgeContent() {
    if (isLoading) {
      return <CircularProgress size={12} />
    }

    if (dotOnly) return ''

    return count > 0 ? count : null
  }
}
